import React, { useEffect, useState, useCallback } from "react";
import { categoryApiService } from "../../../../service/admin/categoryApiService";
import { useLoader } from "../../../../base/context/LoaderProvider";
import { useDispatch, useSelector } from "react-redux";
// global categories are currently unused in this view; fetching code is commented out
import { addItems, categoryData /*, globalCategoriesData, addGlobalCategories */ } from "../../../../redux/slices/categorySlice";
import { ToastTypes, useMessage } from "../../../../base/context/MessageProvider";
import { MdModeEdit, MdSave, MdClose } from "react-icons/md";
import { IoEye, IoEyeOff, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { FaSearch, FaSync } from "react-icons/fa";
import EditCategory from "../../../../component/views/admin/category/editCategory";
import { useNavigate } from "react-router-dom";
import usePermissions from "../../../../hooks/usePermissions";
import "./style.css";

const buildMappedGlobalCategoryPath = (mapping) => {
  if (!mapping || typeof mapping !== "object") return "";

  return [
    mapping.topCategoryName,
    mapping.secondCategoryName,
    mapping.thirdCategoryName
  ].filter(Boolean).join(" > ");
};

export default function ExistingCategory() {
  const [productCounts, setProductCounts] = useState({});
  const [loadingProductCount, setLoadingProductCount] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newProductShow, setNewProductShow] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [previousCategories, setPreviousCategories] = useState([]);
  const [categoryPath, setCategoryPath] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searching, array = search results
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSubId, setLoadingSubId] = useState(null); // id of item whose children are loading
  // const [globalCategories, setGlobalCategories] = useState([]); // disabled until global categories are needed

  const dispatch = useDispatch();
  const categories = useSelector(categoryData) || [];
  // const globalCategoriesFromStore = useSelector(globalCategoriesData); // unused  
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();
  const { canEdit } = usePermissions();

  // Always fetch fresh data from API on mount
  useEffect(() => {
    fetchExistingCategory();
  }, []);

  // const fetchGlobalCategories = useCallback(() => {
  //   categoryApiService.fetchGlobalCategory()
  //     .then((res) => {
  //       if (res.status.code === 200 && res?.data?.length) {
  //         setGlobalCategories(res.data)
  //         dispatch(addGlobalCategories(res.data));
  //       } else if (res.status.code === 402) {
  //         navigate("/login");
  //       } else {
  //         message.showToast(res.error.message, ToastTypes.ERROR);
  //       }
  //     })
  //     .catch((err) => {
  //       message.showToast(err?.message || "Server error fetching global categories", ToastTypes.ERROR);
  //     });
  // }, [dispatch, navigate, message]);

  // parentId optional - when provided fetch children of that category only
  const fetchExistingCategory = useCallback(async (parentId = null) => {
    setLoading(true);

    try {
      const query = parentId ? `?parentId=${parentId}` : "";
      const categoriesRes = await categoryApiService.getExistingCategory(query);
      setLoading(false);

      if (categoriesRes.status.code === 200) {
        // we may now receive `children` arrays with further nesting; store the raw payload
        dispatch(addItems(categoriesRes.data));
      } else if (categoriesRes.status.code === 402) {
        navigate("/login");
      } else {
        message.showToast(categoriesRes.error.message, ToastTypes.ERROR);
      }
    } catch (err) {
      setLoading(false);
      message.showToast(err?.message || "Server error", ToastTypes.ERROR);
    }
  }, [dispatch, navigate, message]);

  // When search results exist, show them; otherwise show current categories
  const filteredCategories = searchResults !== null ? searchResults : categories;

  // Deep search via API
  const handleSearch = useCallback(async () => {
    const q = searchTerm.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await categoryApiService.searchCategoriesDeep(q);
      if (res.status.code === 200) {
        setSearchResults(res.data || []);
      } else if (res.status.code === 402) {
        navigate("/login");
      } else {
        message.showToast(res.error?.message || "Search failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Server error during search", ToastTypes.ERROR);
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm, navigate, message]);

  // Clear search and go back to normal view
  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setSearchResults(null);
  }, []);

  // Handler to fetch product count for a category
  const handleShowProductCount = useCallback(async (item) => {
    const id = item?._id || item?.c_id;
    if (!id) return;
    setLoadingProductCount(prev => ({ ...prev, [id]: true }));
    try {
      const res = await categoryApiService.getCategoryProductCount(id);
      if (res.status.code === 200 && res.data) {
        setProductCounts(prev => ({ ...prev, [id]: res.data.productCount }));
      } else {
        message.showToast(res.error?.message || "Failed to fetch product count", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Server error fetching product count", ToastTypes.ERROR);
    } finally {
      setLoadingProductCount(prev => ({ ...prev, [id]: false }));
    }
  }, [message]);

  const handleSubClick = useCallback(async (item) => {
    if (!item?.c_name) return;
    setPreviousCategories(prev => [...prev, categories]);
    setCategoryPath(prev => [...prev, item.c_name]);
    setSelectedCategory(item.c_name);
    setEditingCategory(null);
    setSearchTerm("");
    // if server already provided nested children use them, otherwise fetch lazily
    if (Array.isArray(item.children) && item.children.length) {
      dispatch(addItems(item.children));
      return;
    }
    // show loader on this row while we fetch
    setLoadingSubId(item._id);
    try {
      await fetchExistingCategory(item._id);
    } finally {
      setLoadingSubId(null);
    }
  }, [categories, fetchExistingCategory, dispatch]);

  const handleBackClick = useCallback(() => {
    if (previousCategories.length > 0) {
      const newPrev = [...previousCategories];
      const lastCategories = newPrev.pop();
      dispatch(addItems(lastCategories));
      setPreviousCategories(newPrev);
      setCategoryPath(prev => prev.slice(0, -1));
      setSelectedCategory("");
      setEditingCategory(null);
      setSearchTerm("");
    }
  }, [previousCategories, dispatch]);

  const startEditingCategory = useCallback((item) => {
    if (!item?.c_name) return;
    setEditingCategory(item._id);
    setNewCategoryName(item.c_name);
    setNewProductShow(item.shownProductLimit || 50);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingCategory(null);
    setNewCategoryName("");
    setNewProductShow("");
  }, []);

  const handleUpdateCategory = useCallback((clickedCategoryName, hide) => {
    if (!clickedCategoryName) return;
    const payload = {
      oldCategoryName: categoryPath.concat(clickedCategoryName).join("/"),
      newCategory: { hide: !hide },
    };
    loader.showLoading(true);
    categoryApiService.updateCategory(JSON.stringify(payload))
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          dispatch(addItems([])); // Clear cache to force refresh
          fetchExistingCategory();
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
      });
  }, [categoryPath, loader, message, dispatch, fetchExistingCategory]);

  const handleOnClick = useCallback((c_name) => {
    if (!c_name) return;
    setOpen(true);
    setSelectedCategory(categoryPath.concat(c_name).join("/"));
  }, [categoryPath]);

  const handleContentClick = useCallback((clickedCategoryName, contentId) => {
    const fullCategoryName = categoryPath.concat(clickedCategoryName).join("/");
    if (!contentId) {
      const payload = { oldCategoryName: fullCategoryName, newCategory: {} };
      categoryApiService.createBlankCategoryContent(JSON.stringify(payload))
        .then((res) => {
          if (res.status.code === 200) {
            navigate(`/admin/categories/content?id=${res.data._id}`);
          } else {
            message.showToast(res.error.message, ToastTypes.ERROR);
          }
        })
        .catch((err) => {
          message.showToast(err?.message || "Server error", ToastTypes.ERROR);
        });
    } else {
      navigate(`/admin/categories/content?id=${contentId}`);
    }
  }, [categoryPath, navigate, message]);

  const saveCategoryName = useCallback((clickedCategoryName) => {
    if (!clickedCategoryName) return;
    const fullCategoryName = categoryPath.concat(clickedCategoryName).join("/");
    const payload = {
      oldCategoryName: fullCategoryName,
      newCategory: { c_name: newCategoryName, shownProductLimit: newProductShow },
    };
    loader.showLoading(true);
    categoryApiService.updateCategory(JSON.stringify(payload))
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          dispatch(addItems([]));
          fetchExistingCategory();
          setEditingCategory(null);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
      });
  }, [categoryPath, newCategoryName, newProductShow, loader, message, dispatch, fetchExistingCategory]);

  const handleOnClickClose = useCallback(() => setOpen(false), []);

  const handleMapGlobalCategory = useCallback((categoryItem) => {
    const fullCategoryName = categoryPath.concat(categoryItem.c_name).join("/");

    // Prepare category data to pass via URL params for new tab
    const categoryData = {
      categoryId: categoryItem.c_id || categoryItem._id,
      categoryName: fullCategoryName,
      globalCategoryId: categoryItem.globalCategoryId,
      globalCategoryPath: categoryItem.globalCategoryPath,
      bcd: categoryItem.bcd,
      gst_percentage: categoryItem.gst_percentage,
      hsn_code: categoryItem.hsn_code
    };

    // Create URL with query parameters - only send categoryId and categoryName
    const params = new URLSearchParams();
    params.append('categoryId', categoryData.categoryId);
    params.append('categoryName', categoryData.categoryName);

    const url = `/admin/global-category-mapping?${params.toString()}`;

    // Open in new tab
    window.open(url, '_blank');
  }, [categoryPath]);

  return (
    <div className="cat-page">
      {/* Header */}
      <div className="cat-header">
        <div className="cat-header-left">
          {previousCategories.length > 0 && (
            <button className="cat-back-btn" onClick={handleBackClick}>
              <IoChevronBack /> Back
            </button>
          )}
          {categoryPath.length > 0 && (
            <div className="cat-breadcrumb">
              {categoryPath.map((p, i) => (
                <p key={i} style={{ display: 'contents' }}>
                  {i > 0 && <IoChevronForward className="cat-breadcrumb-sep" />}
                  <span className="cat-breadcrumb-item">{p}</span>
                </p>
              ))}
            </div>
          )}
        </div>
        <div className="cat-header-right">
          <div className="cat-search-box">
            <input
              type="text"
              className="cat-search-input"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            {searchResults !== null && (
              <button className="cat-search-clear-btn" onClick={clearSearch} title="Clear search">
                <MdClose />
              </button>
            )}
            <button
              className="cat-search-btn"
              onClick={handleSearch}
              disabled={searchLoading || !searchTerm.trim()}
              title="Search"
            >
              {searchLoading ? "..." : <FaSearch />}
            </button>
          </div>
          <button
            className="cat-refresh-btn"
            onClick={() => {
              setCategoryPath([]);
              setPreviousCategories([]);
              clearSearch();
              fetchExistingCategory(null);
            }}
            title="Refresh"
          >
            <FaSync className={loading ? "cat-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="cat-table-container">
        <table className="cat-table">
          <thead>
            <tr>
              <th className="cat-th-name">Categories</th>
              <th className="cat-th-id">Category Id</th>
              <th className="cat-th-count">Products</th>
              {/* <th className="cat-th-limit">Product Show</th> */}
              <th className="cat-th-sub">Sub-Categories</th>
              <th className="cat-th-sub">Global Category Path</th>
              {searchResults !== null && <th className="cat-th-path">Path</th>}
              <th className="cat-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length === 0 ? (
              <tr>
                <td colSpan={searchResults !== null ? 8 : 7} className="cat-empty">
                  {loading ? "Loading..." : searchTerm ? "No categories found" : "No categories available"}
                </td>
              </tr>
            ) : (
              filteredCategories.map((item) => {
                if (item == null) return null;
                // undefined means the item is invalid; empty string is allowed but shown as N/A
                if (item.c_name === undefined) return null;
                const displayName = item.c_name === "" ? "N/A" : item.c_name;
                const isEditing = editingCategory === item._id;
                return (
                  <tr key={item._id} className={item.hide ? "cat-row-hidden" : ""}>
                    <td className="cat-td-name">
                      {isEditing ? (
                        <input
                          type="text"
                          className="cat-edit-input"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <span className={`${item.hide ? "cat-name-hidden" : "cat-name"} cat-name-hover`}>
                          {displayName}
                          <div className="cat-name-tooltip">
                            <div className="cat-tooltip-heading">Pricing Algo Details</div>
                            <div className="cat-tooltip-grid">
                              <span>Length:</span><span>{item.length ?? '-'}cm</span>
                              <span>Breadth:</span><span>{item.breadth ?? '-'}cm</span>
                              <span>Height:</span><span>{item.height ?? '-'}cm</span>
                              <span>Weight:</span><span>{item.weight ?? '-'}gram</span>
                              <span>Min Weight:</span><span>{item.minWeight ?? '-'}gram</span>
                              <span>Max Weight:</span><span>{item.maxWeight ?? '-'}gram</span>
                              <span>Priority:</span><span>{item.priority ?? '-'}</span>
                              <span>BCD:</span><span>{item.bcd ?? '-'}%</span>
                              <span>Platform Fee:</span><span>{item.platformFee ?? '-'}%</span>
                              <span>Intl Freight/Kg:</span><span>{item.interationalFreightPerKg ?? '-'}</span>
                              <span>Local Shipping:</span><span>{item.localShipping ?? '-'} (RMB)</span>
                              <span>Markup:</span><span>{item.markUp ?? '-'}%</span>
                            </div>
                          </div>
                        </span>
                      )}
                    </td>
                    <td className="cat-td-id">{item.c_id || 'N/A'}</td>
                    <td className="cat-td-count">
                      {productCounts[item._id || item.c_id] !== undefined ? (
                        <span>{productCounts[item._id || item.c_id]}</span>
                      ) : (
                        <button
                          className="cat-action-btn cat-btn-count"
                          onClick={() => handleShowProductCount(item)}
                          disabled={!(item._id || item.c_id) || loadingProductCount[item._id || item.c_id]}
                          title="Show Product Count"
                        >
                          {loadingProductCount[item._id || item.c_id] ? "..." : "Show Count"}
                        </button>
                      )}
                    </td>
                    {/* <td className="cat-td-limit">
                      {isEditing ? (
                        <input
                          type="number"
                          className="cat-edit-input cat-edit-input-small"
                          value={newProductShow}
                          onChange={(e) => setNewProductShow(e.target.value)}
                        />
                      ) : (
                        <span className="cat-limit">{item.shownProductLimit || 50}</span>
                      )}
                    </td> */}
                    <td className="cat-td-sub">
                      {((item.childCount != null && item.childCount > 0) || (Array.isArray(item.children) && item.children.length > 0)) ? (
                        <button
                          className="cat-sub-link"
                          onClick={() => handleSubClick(item)}
                          disabled={loadingSubId === item._id}
                        >
                          {loadingSubId === item._id ? (
                            <span className="cat-loading-spinner" />
                          ) : (
                            <>Sub Categories<span className="cat-sub-count">({
                              item.childCount != null
                                ? item.childCount
                                : Array.isArray(item.children)
                                  ? item.children.length
                                  : 0
                            })</span></>
                          )}
                        </button>
                      ) : (
                        <span className="cat-no-sub">-</span>
                      )}
                    </td>
                    <td className="cat-td-path">
                      {(Array.isArray(item?.globalCategories) && item.globalCategories.length > 0) || item?.globalCategoryPath ? (
                        <div className="cat-path-cell">
                          {(() => {
                            const paths = Array.isArray(item.globalCategories) && item.globalCategories.length > 0
                              ? item.globalCategories.map(buildMappedGlobalCategoryPath).filter(Boolean)
                              : Array.isArray(item.globalCategoryPath)
                                ? item.globalCategoryPath
                                : [item.globalCategoryPath];

                            return (
                              <>
                                <span className="cat-path-preview">
                                  Mapped Global Category ({paths?.length > 1 ? paths.length : 1})
                                </span>
                                <div className="cat-path-tooltip">
                                  <ul className="cat-path-list">
                                    {paths.map((path, idx) => (
                                      <li key={idx} className="cat-path-item">{path}</li>
                                    ))}
                                  </ul>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <span className="cat-no-path">N/A</span>
                      )}
                    </td>
                    {searchResults !== null && (
                      <td className="cat-td-path">
                        <span className="cat-search-path" title={item.searchPath}>{item.searchPath || '-'}</span>
                      </td>
                    )}
                    <td className="cat-td-actions">
                      <div className="cat-actions">
                        {canEdit("categories") && (
                          <>
                            <button
                              className={`cat-action-btn ${item.hide ? "cat-btn-show" : "cat-btn-hide"}`}
                              onClick={() => handleUpdateCategory(item.c_name, item.hide)}
                              title={item.hide ? "Show" : "Hide"}
                            >
                              {item.hide ? <IoEye /> : <IoEyeOff />}
                            </button>
                            {isEditing ? (
                              <>
                                <button className="cat-action-btn cat-btn-save" onClick={() => saveCategoryName(item.c_name)} title="Save">
                                  <MdSave />
                                </button>
                                <button className="cat-action-btn cat-btn-cancel" onClick={cancelEditing} title="Cancel">
                                  <MdClose />
                                </button>
                              </>
                            ) : (
                              <button className="cat-action-btn cat-btn-edit" onClick={() => startEditingCategory(item)} title="Edit">
                                <MdModeEdit />
                              </button>
                            )}
                          </>
                        )}
                        <button
                          className="cat-content-btn"
                          onClick={() => handleContentClick(item.c_name, item.contentId)}
                        >
                          Html Content
                        </button>
                        <button
                          className="cat-content-btn"
                          onClick={() => handleMapGlobalCategory(item)}
                        >
                          Map Global Category
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Stats */}
      <div className="cat-footer">
        <span className="cat-stats">
          Showing {filteredCategories.length} of {categories.length} categories
          {categoryPath.length > 0 && ` in "${categoryPath[categoryPath.length - 1]}"`}
        </span>
      </div>

      <EditCategory
        open={open}
        handleClose={handleOnClickClose}
        category={selectedCategory}
        categories={categories}
        ExistingCategory={fetchExistingCategory}
      />
    </div>
  );
}
